import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { atomWithDefault } from 'jotai/utils';
import { createContext } from 'react';
import { v4 as uuid } from 'uuid';

import { OrderTypeEnum } from '@flipster/apis/types';
import { estimateOrderLiqPrice } from '@flipster/features/src/shared/liquidation';
import { orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import { positionBracketBunja } from '@flipster/features/src/shared/position-bracket/state';
import { snapshotSpecBunja } from '@flipster/features/src/shared/snapshot/state';
import { addPositionMutationsBunja } from '@flipster/features/src/shared/trade-actions/add-position/state';
import { tradeModeBunja } from '@flipster/features/src/trade-mode/state';
import { createPositionOrDismissAtom } from '@flipster/features/src/utils/position-or-dismiss';
import { i18nBunja } from '@flipster/i18n';
import type { OpenPosition } from '@flipster/r230522/normal-sub/tables/private/positions';
import { marginsBunja } from '@flipster/r230522/original/margin';
import { marketsBunja } from '@flipster/r230522/original/market';
import { openPositionsBunja } from '@flipster/r230522/original/position';
import type { OpenPosition as LegacyOpenPosition } from '@flipster/r230522/original/position';
import { pswapTickersBunja } from '@flipster/r230522/original/pswap-ticker';
import { pswapsBunja } from '@flipster/r230522/original/pswaps';
import { getPositionDisplayPrice, getTickerDisplayPriceOrDefault } from '@flipster/r230522/original/ticker-price';
import { snackbar } from '@flipster/react/design-system/snackbar/index';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import { dateToEpochString } from '@flipster/utils/datetime';
import { getErrorMessage } from '@flipster/utils/error';
import { getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';
import Num, { BigNumber, numberFormatter } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';

export const AddPositionModalContext = createContext<OpenPosition | LegacyOpenPosition | null>(null);
export const AddPositionModalScope = createScopeFromContext(AddPositionModalContext);

export const addPositionModalBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { getT } = bunja.use(i18nBunja);
  const initPosition = bunja.use(AddPositionModalScope);
  const { openPositionsAtom } = bunja.use(openPositionsBunja);
  const { getMarginAtom } = bunja.use(marginsBunja);
  const { instrumentsKeyBySymbolAtom } = bunja.use(snapshotSpecBunja);
  const { pswapsAtom } = bunja.use(pswapsBunja);
  // WS??specs 브래킷 해석은 positionBracketBunja 한 곳에서만 — 같은 모달의 validation-state.ts와
  // 동일한 브래킷 atom을 consume한다 (ERD-240 §10, 단일 해석 지점).
  const { positionBracketAtom } = bunja.use(positionBracketBunja);
  const { pswapTickersAtom } = bunja.use(pswapTickersBunja);
  const { marketsAtom } = bunja.use(marketsBunja);
  const { tradeModeAtom } = bunja.use(tradeModeBunja);
  const isOneWayAtom = atom((get) => get(tradeModeAtom) === 'OneWay');

  const t = getT();

  // 실행(mutation)은 trade-actions 데이터 레이어에서 — 페이로드 조립/토스트/segment는 이 모달 bunja가 유지.
  const { postAddPositionApiAtom, postOneWayAddPositionApiAtom } = bunja.use(addPositionMutationsBunja);

  const tabAtom = atom<'market' | 'limit'>('market');
  const inputModeAtom = atomWithDefault<'qty' | 'positionSize' | 'margin'>(() => {
    const validInputModes = ['qty', 'positionSize', 'margin'] as const;
    const stored = getLocalStorage('flipster.configs.v1.addPositionModal.orderTypeSelect');
    return stored && validInputModes.includes(stored) ? stored : 'qty';
  });

  const inputValueAtom = atom<string>('');
  const limitPriceAtom = atom<string>('');
  const isBboCheckedAtom = atom<boolean>(false);
  const isBboAtom = atom((get) => get(isBboCheckedAtom) && get(tabAtom) === 'limit');

  const targetPrecisionAtom = atom((get) => {
    const inputMode = get(inputModeAtom);
    const instrument = get(instrumentAtom);
    return inputMode === 'qty' ? instrument.tradeQtyPrecision : getQuoteCurrencyPrecision(instrument.quoteCurrency);
  });

  const targetUnitAtom = atom((get) => {
    const inputMode = get(inputModeAtom);
    const instrument = get(instrumentAtom);
    return inputMode === 'qty' ? instrument.positionUnit : instrument.quoteCurrency;
  });

  const marginAtom = atom((get) => {
    const inputMode = get(inputModeAtom);
    const inputValue = get(inputValueAtom);
    const position = get(positionAtom);
    const targetPrice = get(targetPriceAtom);

    if (!inputValue) return '0';

    switch (inputMode) {
      case 'margin':
        return inputValue;
      case 'positionSize':
        return Num(inputValue).dividedBy(position.initLeverage).toString();
      case 'qty':
        if (!targetPrice || Num(targetPrice).isZero()) return '0';
        return Num(inputValue).multipliedBy(targetPrice).dividedBy(position.initLeverage).toString();
      default:
        return '0';
    }
  });

  const positionSizeAtom = atom((get) => {
    const inputMode = get(inputModeAtom);
    const inputValue = get(inputValueAtom);
    const position = get(positionAtom);
    const targetPrice = get(targetPriceAtom);

    if (!inputValue) return '0';

    switch (inputMode) {
      case 'margin':
        return Num(inputValue).multipliedBy(position.initLeverage).toString();
      case 'positionSize':
        return inputValue;
      case 'qty':
        if (!targetPrice || Num(targetPrice).isZero()) return '0';
        return Num(inputValue).multipliedBy(targetPrice).toString();
      default:
        return '0';
    }
  });

  const qtyAtom = atom((get) => {
    const inputMode = get(inputModeAtom);
    const inputValue = get(inputValueAtom);
    const position = get(positionAtom);
    const targetPrice = get(targetPriceAtom);

    if (!inputValue || !targetPrice || Num(targetPrice).isZero()) return '0';

    switch (inputMode) {
      case 'margin':
        return Num(inputValue).multipliedBy(position.initLeverage).dividedBy(targetPrice).toString();
      case 'positionSize':
        return Num(inputValue).dividedBy(targetPrice).toString();
      case 'qty':
        return inputValue;
      default:
        return '0';
    }
  });

  const positionAtom = createPositionOrDismissAtom<LegacyOpenPosition>({
    openPositionsAtom,
    symbol: initPosition?.symbol,
    slot: initPosition?.slot,
  });

  const instrumentAtom = atom((get) => {
    const position = get(positionAtom);
    const instrumentsKeyBySymbol = get(instrumentsKeyBySymbolAtom);
    const instrument = instrumentsKeyBySymbol?.[position.symbol];
    if (!instrument) throw new Error(`Instrument not found: ${position.symbol}`);
    return instrument;
  });

  const bboPriceAtom = atom((get) => {
    const position = get(positionAtom);
    return position.side === 'Long' ? position.askPrice : position.bidPrice;
  });

  const targetPriceAtom = atom((get) => {
    const tab = get(tabAtom);
    const limitPrice = get(limitPriceAtom);
    const bboPrice = get(bboPriceAtom);
    const isBbo = get(isBboAtom);
    switch (tab) {
      case 'limit':
        if (isBbo) return bboPrice;
        return limitPrice;
      case 'market':
        return bboPrice;
      default:
        return bboPrice;
    }
  });

  const availableFundsAtom = atom((get) => {
    const position = get(positionAtom);
    const margins = get(getMarginAtom);
    const instrument = get(instrumentAtom);

    switch (position.marginType) {
      case 'Cross':
        return BigNumber.max(0, Num(margins[instrument.quoteCurrency]?.crossMarginAvailable || '0')).toFixed(2);
      case 'Isolated':
        return BigNumber.max(0, Num(margins[instrument.quoteCurrency]?.isolatedMarginAvailable || '0')).toFixed(2);
      default:
        return '0';
    }
  });

  // total value
  const existingTotalValueAtom = atom((get) => {
    const inputMode = get(inputModeAtom);
    const position = get(positionAtom);

    switch (inputMode) {
      case 'qty':
        return Num(position.quantity).abs().toString();
      case 'margin':
      case 'positionSize':
      default:
        // existingTotalValue이므로 targetPrice대신 display reference price를 사용하는 것이 맞다.
        return Num(position.quantity).abs().multipliedBy(getPositionDisplayPrice(position)).toString();
    }
  });

  const addingTotalValueAtom = atom((get) => {
    const inputMode = get(inputModeAtom);
    return inputMode === 'qty' ? get(qtyAtom) : get(positionSizeAtom);
  });

  const estTotalValueAtom = atom((get) => {
    const existingTotalValue = get(existingTotalValueAtom);
    const addingTotalValue = get(addingTotalValueAtom);
    return Num(existingTotalValue).plus(addingTotalValue).toString();
  });

  const markPriceAtom = atom((get) => {
    const position = get(positionAtom);
    return (
      get(pswapTickersAtom)?.[position.symbol]?.markPrice ??
      get(marketsAtom)?.[position.symbol]?.markPrice ??
      (getTickerDisplayPriceOrDefault(get(pswapTickersAtom)?.[position.symbol], get(marketsAtom)?.[position.symbol]) ||
        getPositionDisplayPrice(position))
    );
  });

  const liqPriceFeeRateAtom = atom((get) => {
    const instrument = get(instrumentAtom);
    const pswap = get(pswapsAtom)?.[instrument.symbol];
    return BigNumber.max(pswap?.takerFeeRate ?? '0', pswap?.makerFeeRate ?? '0').toString();
  });

  const liqPriceSharedArgsAtom = atom((get) => {
    const position = get(positionAtom);
    const instrument = get(instrumentAtom);
    const margin = get(getMarginAtom)[instrument.quoteCurrency];
    return {
      markPrice: get(markPriceAtom),
      targetSymbol: position.symbol,
      // Cross liq needs the balance (`marginBalance`), not `crossMarginAvailable`
      // (= balance − reserved init margin), which would inflate the liq price.
      crossMarginBalance: margin?.marginBalance ?? '0',
      maintPositionMargin: margin?.maintPositionMargin,
      tickSize: instrument.tickSize,
    };
  });

  const existingLiquidationPriceAtom = atom((get) => {
    const position = get(positionAtom);
    return position.liquidationPrice;
  });

  /**
   * Hypothetical liq price for the *added* portion in isolation. Modeled as
   * opening a fresh position with the form's (qty, price, leverage), so the
   * row stays informative even when the existing position is empty.
   */
  const addingLiquidationPriceAtom = atom((get) => {
    const position = get(positionAtom);
    const positionBracket = get(positionBracketAtom);
    if (!positionBracket) return null;

    return estimateOrderLiqPrice({
      ...get(liqPriceSharedArgsAtom),
      targetPosition: null,
      positionBracket,
      order: {
        side: position.side,
        qty: get(qtyAtom),
        price: get(targetPriceAtom),
        initLeverage: position.initLeverage,
        feeRate: get(liqPriceFeeRateAtom),
        marginMode: position.marginType,
      },
    });
  });

  const estLiquidationPriceAtom = atom((get) => {
    const position = get(positionAtom);
    const positionBracket = get(positionBracketAtom);
    // ref: https://prex.atlassian.net/browse/QA-18973
    // input이 0인 경우, 계산하지 않고 position의 liq price 값을 그대로 씀
    if (Num(get(marginAtom)).isZero()) return position.liquidationPrice ?? null;
    if (!positionBracket) return null;

    return estimateOrderLiqPrice({
      ...get(liqPriceSharedArgsAtom),
      targetPosition: position,
      positionBracket,
      order: {
        side: position.side,
        qty: get(qtyAtom),
        price: get(targetPriceAtom),
        initLeverage: position.initLeverage,
        feeRate: get(liqPriceFeeRateAtom),
        marginMode: position.marginType,
      },
    });
  });

  // entry price
  const existingEntryPriceAtom = atom((get) => {
    const position = get(positionAtom);
    return position.avgEntryPrice;
  });

  const addingEntryPriceAtom = atom((get) => {
    const targetPrice = get(targetPriceAtom);
    return targetPrice;
  });

  const estEntryPriceAtom = atom((get) => {
    const position = get(positionAtom);
    // qty인 경우에도 positionSize로 계산하기 위해서 positionSize로 고정한다.
    const addingTotalValue = Num(get(positionSizeAtom));
    const addingEntryPrice = get(addingEntryPriceAtom);

    if (!addingEntryPrice || Num(addingEntryPrice).isZero()) return position.avgEntryPrice;

    const absPosition = Num(position.position).abs();
    const entryPrice = Num(position.avgEntryPrice);

    const weightedExisting = entryPrice.multipliedBy(absPosition);
    const totalSize = absPosition.plus(addingTotalValue.dividedBy(addingEntryPrice));

    if (totalSize.isZero()) return position.avgEntryPrice;
    return weightedExisting.plus(addingTotalValue).dividedBy(totalSize).toString();
  });

  const isLoadingAtom = atom((get) => {
    const isOneWay = get(isOneWayAtom);
    const oneWayMutation = get(postOneWayAddPositionApiAtom);
    const normalMutation = get(postAddPositionApiAtom);
    return isOneWay ? oneWayMutation.isPending : normalMutation.isPending;
  });

  const addPosition = async () => {
    try {
      const position = store.get(positionAtom);
      const instrument = store.get(instrumentAtom);
      const targetPrice = store.get(targetPriceAtom);
      const isOneWay = store.get(isOneWayAtom);
      const attribution = store.get(orderAttributionAtom);
      const isBbo = store.get(isBboAtom);
      const tab = store.get(tabAtom);
      const inputMode = store.get(inputModeAtom);

      const price = isBbo ? undefined : targetPrice;

      const requestId = uuid();
      const timestamp = dateToEpochString(new Date());
      const amount = inputMode !== 'qty' ? store.get(positionSizeAtom) : undefined;
      const qty = inputMode === 'qty' ? store.get(qtyAtom) : undefined;

      if (isOneWay) {
        const oneWayMutation = store.get(postOneWayAddPositionApiAtom);
        const { data } = await oneWayMutation.mutateAsync({
          symbol: position.symbol,
          params: {
            side: position.side,
            amount,
            qty,
            price,
            reduceOnly: false,
            requestId,
            timestamp,
            attribution,
            orderType: tab === 'market' ? OrderTypeEnum['Market'] : OrderTypeEnum['Limit'],
            priceMatchStrategy: isBbo ? 'PRICE_MATCH_STRATEGY_COUNTERPARTY_1' : undefined,
          },
        });

        const isIoc = tab === 'market' && data.order?.remainingQty && Num(data.order.remainingQty).isGreaterThan(0);
        if (isIoc) {
          const successAmount = BigNumber.max(
            Num(data.order?.size || '0')
              .abs()
              .minus(Num(data.order?.remainingQty || '0').abs()),
            0,
          );
          const remainingAmount = Num(data.order?.remainingQty || '0').abs();

          snackbar.error({
            message: t('Trade.r260224.Market_order_partial_fill_toast', {
              amountSymbolFilled: `${numberFormatter(successAmount, {
                precision: instrument.tradeQtyPrecision,
                toFixed: true,
              })} ${instrument.positionUnit}`,
              value: numberFormatter(data.order?.price || '0', {
                precision: instrument.pricePrecision,
                toFixed: true,
              }),
              amountSymbolRemaining: `${numberFormatter(remainingAmount, {
                precision: instrument.tradeQtyPrecision,
                toFixed: true,
              })} ${instrument.positionUnit}`,
            }),
          });
        }
      } else {
        // multi position mode
        const normalMutation = store.get(postAddPositionApiAtom);
        const { data } = await normalMutation.mutateAsync({
          symbol: position.symbol,
          slot: position.slot,
          params: {
            side: position.side,
            amount,
            qty,
            price,
            requestId,
            timestamp,
            attribution,
            orderType: tab === 'market' ? OrderTypeEnum['Market'] : OrderTypeEnum['Limit'],
            priceMatchStrategy: isBbo ? 'PRICE_MATCH_STRATEGY_COUNTERPARTY_1' : undefined,
          },
        });

        const isIoc = tab === 'market' && data.order?.remainingQty && Num(data.order.remainingQty).isGreaterThan(0);
        if (isIoc) {
          const successAmount = BigNumber.max(
            Num(data.order?.size || '0')
              .abs()
              .minus(Num(data.order?.remainingQty || '0').abs()),
            0,
          );
          const remainingAmount = Num(data.order?.remainingQty || '0').abs();

          snackbar.error({
            message: t('Trade.r260224.Market_order_partial_fill_toast', {
              amountSymbolFilled: `${numberFormatter(successAmount, {
                precision: instrument.tradeQtyPrecision,
                toFixed: true,
              })} ${instrument.positionUnit}`,
              value: numberFormatter(data.order?.price || '0', {
                precision: instrument.pricePrecision,
                toFixed: true,
              }),
              amountSymbolRemaining: `${numberFormatter(remainingAmount, {
                precision: instrument.tradeQtyPrecision,
                toFixed: true,
              })} ${instrument.positionUnit}`,
            }),
          });
        }
      }

      setLocalStorage('flipster.configs.v1.addPositionModal.orderTypeSelect', inputMode);

      // market order인 경우에는 server side에서 snackbar를 표시함
      if (tab === 'limit') {
        snackbar.success({ message: t('Trade.r251104.Your_limit_order_has_been_placed') });
      }
    } catch (error) {
      snackbar.error({ message: getErrorMessage(error) });
      throw error;
    }
  };

  const handleSegmentTrack = (step: 'input' | 'review') => {
    const orderByToken = (() => {
      const inputMode = store.get(inputModeAtom);
      switch (inputMode) {
        case 'margin':
          return 'margin';
        case 'positionSize':
          return 'position_size';
        case 'qty':
          return 'quantity';
      }
    })();

    switch (step) {
      case 'input': {
        const isBbo = store.get(isBboAtom);
        segment.track(TradeEvent.AddPositionReviewClick, {
          symbol: store.get(positionAtom)!.symbol,
          side: store.get(positionAtom)!.side === 'Long' ? 'buy' : 'sell',
          leverage: store.get(positionAtom)!.initLeverage,
          order_by_token: orderByToken,
          add_amount: Number(store.get(inputValueAtom)),
          order_type: store.get(tabAtom),
          limit_price: isBbo ? null : Number(store.get(limitPriceAtom)),
          bbo_price: isBbo,
        });
        break;
      }
      case 'review': {
        segment.track(TradeEvent.AddPositionConfirmClick, {
          symbol: store.get(positionAtom)!.symbol,
          side: store.get(positionAtom)!.side === 'Long' ? 'buy' : 'sell',
          leverage: store.get(positionAtom)!.initLeverage,
          order_by_token: orderByToken,
          estimated_position_amount: Number(store.get(estTotalValueAtom)),
          existing_position_amount: Number(store.get(existingTotalValueAtom)),
          estimated_liquidation_price: Number(store.get(estLiquidationPriceAtom)),
          existing_liquidation_price: Number(store.get(existingLiquidationPriceAtom)),
          estimated_entry_price: Number(store.get(estEntryPriceAtom)),
          existing_entry_price: Number(store.get(existingEntryPriceAtom)),
        });
        break;
      }
    }
  };

  bunja.effect(() => {
    let prevMode = store.get(inputModeAtom);

    const unsubInputMode = store.sub(inputModeAtom, () => {
      const newMode = store.get(inputModeAtom);

      const currentInputValue = store.get(inputValueAtom);

      if (!currentInputValue) {
        prevMode = newMode;
        return;
      }

      const position = store.get(positionAtom);
      const instrument = store.get(instrumentAtom);
      const targetPrice = store.get(targetPriceAtom);
      const quotePrecision = getQuoteCurrencyPrecision(instrument.quoteCurrency);

      // prevMode 기준 inputValue → margin 변환
      let margin: BigNumber;
      switch (prevMode) {
        case 'margin': {
          margin = Num(currentInputValue);
          break;
        }
        case 'positionSize': {
          margin = Num(currentInputValue).dividedBy(position.initLeverage);
          break;
        }
        case 'qty': {
          if (!targetPrice || Num(targetPrice).isZero()) {
            margin = Num(0);
            break;
          }
          margin = Num(currentInputValue).multipliedBy(targetPrice).dividedBy(position.initLeverage);
          break;
        }
        default: {
          margin = Num(0);
          break;
        }
      }

      // margin → newMode 단위로 변환
      let newValue: string;
      switch (newMode) {
        case 'margin':
          newValue = margin.toFixed(quotePrecision, BigNumber.ROUND_DOWN);
          break;
        case 'positionSize':
          newValue = margin.multipliedBy(position.initLeverage).toFixed(quotePrecision, BigNumber.ROUND_DOWN);
          break;
        case 'qty': {
          if (!targetPrice || Num(targetPrice).isZero()) {
            newValue = '0';
            break;
          }
          newValue = margin
            .multipliedBy(position.initLeverage)
            .dividedBy(targetPrice)
            .toFixed(instrument.tradeQtyPrecision, BigNumber.ROUND_DOWN);
          break;
        }
        default: {
          newValue = '0';
          break;
        }
      }

      store.set(inputValueAtom, newValue);
      prevMode = newMode;
    });

    const unsubTab = store.sub(tabAtom, () => {
      if (store.get(tabAtom) === 'limit') {
        store.set(limitPriceAtom, store.get(bboPriceAtom));
      }
    });
    const unsubIsBbo = store.sub(isBboAtom, () => {
      if (store.get(isBboAtom)) {
        store.set(limitPriceAtom, store.get(bboPriceAtom));
      }
    });

    return () => {
      unsubInputMode();
      unsubTab();
      unsubIsBbo();
    };
  });

  return {
    // core state
    positionAtom,
    inputModeAtom,
    tabAtom,
    instrumentAtom,
    inputValueAtom,
    isBboAtom,
    isBboCheckedAtom,
    targetPriceAtom,
    bboPriceAtom,
    limitPriceAtom,
    targetPrecisionAtom,
    targetUnitAtom,
    // derived values
    marginAtom,
    positionSizeAtom,
    qtyAtom,
    availableFundsAtom,
    // display
    existingTotalValueAtom,
    addingTotalValueAtom,
    estTotalValueAtom,
    existingLiquidationPriceAtom,
    addingLiquidationPriceAtom,
    estLiquidationPriceAtom,
    existingEntryPriceAtom,
    addingEntryPriceAtom,
    estEntryPriceAtom,
    // actions
    isLoadingAtom,
    addPosition,
    handleSegmentTrack,
  };
});
addPositionModalBunja.debugLabel = 'addPositionModalBunja';
